import React, { useCallback, type ReactNode } from 'react';
import SectionMessage from '@atlaskit/section-message/message';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Link from '@atlaskit/link/link';
import Code from '@atlaskit/code/code';
import CodeBlock from '@atlaskit/code/code-block';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type { ApplicationLinkV3Status } from './CommonApplicationLinkCreationEntity.tsx';
import { lookupTypeByTypeId } from './TypeDefinition.types.tsx';
import type { ApplinksError } from './Errors.tsx';

import AuthLevelMismatchDescription from './AuthLevelMismatchDescription.tsx';

const cloudConnectorAwaitingDcResponseTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.cloud-connector-awaiting-dc-response-status-title',
	defaultMessage: 'Awaiting DC response',
	description: 'Title for cloud connector awaiting DC response message',
});

const cloudConnectorAwaitingDcResponseDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.cloud-connector-awaiting-dc-response-status-description',
	defaultMessage:
		'This application link is managed by cloud connector, and is awaiting admin response from <link>{applicationName}</link>.',
	description: 'Description for cloud connector awaiting DC response message',
});

const connectionRefusedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.connection-refused-title',
	defaultMessage: 'The remote application is not responding',
	description:
		'The text is shown as a heading in the status detail panel for an application link when the remote application refuses the connection or is unreachable.',
});

const connectionRefusedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.connection-refused-description',
	defaultMessage:
		'The connection to <code>{rpcUrl}</code> was refused. <link>{applicationName}</link> may be down or blocked by a firewall. Alternatively, the Application URL for the remote application may be incorrect or may have changed recently.',
	description:
		'Description for connection refused message. Placeholders: rpcUrl = URL of application, applicationName = Name of application',
});

const unknownHostTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.unknown-host-title',
	defaultMessage: 'Unable to reach the remote application',
	description:
		'The text is shown as a heading in the status detail panel for an application link when the hostname of the remote application cannot be resolved via DNS.',
});

const unknownHostDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.unknown-host-description',
	defaultMessage:
		"We couldn't resolve the hostname at <code>{rpcUrl}</code>. There may be a DNS configuration problem, or the Application URL for the remote application may be incorrect or may have changed recently.",
	description:
		'The text is shown as a body paragraph in the status detail panel when the hostname cannot be resolved. The placeholder {rpcUrl} will be substituted with the URL of the remote application.',
});

const sslUntrustedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.ssl-untrusted-title',
	defaultMessage: "The remote certificate can't be trusted",
	description: 'Title for untrusted SSL certificate message',
});

const sslUntrustedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.ssl-untrusted-description',
	defaultMessage:
		"<link>{applicationName}</link> may be using a self-signed SSL certificate or a certificate that was issued by a certificate authority that isn't known locally.",
	description: 'Description for untrusted SSL certificate message',
});

const sslHostnameUnmatchedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.ssl-hostname-unmatched-title',
	defaultMessage: "The remote certificate doesn't match the remote host name",
	description:
		'The text is shown as a heading in the status detail panel for an application link when the SSL certificate presented by the remote server does not match its hostname.',
});

const sslHostnameUnmatchedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.ssl-hostname-unmatched-description',
	defaultMessage:
		"The SSL certificate used by <link>{applicationName}</link> can't be trusted because the certificate's host name doesn't match the URL {rpcUrl}.",
	description: 'Description for SSL hostname mismatch message',
});

const sslUnmatchedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.ssl-unmatched-title',
	defaultMessage: 'Unable to verify remote certificate details',
	description: 'Title for unmatched SSL certificate message',
});

const sslUnmatchedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.ssl-unmatched-description',
	defaultMessage:
		"The SSL certificate used by <link>{applicationName}</link> can't be trusted because details inside the certificate can't be verified. Check that the remote certificate has the correct details.",
	description: 'Description for unmatched SSL certificate message',
});

const oauthProblemTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.oauth-problem-title',
	defaultMessage: 'OAuth problem',
	description:
		'The text is shown as a heading in the status detail panel for an application link when there is a general OAuth configuration problem preventing the connection.',
});

const oauthProblemDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.oauth-problem-description',
	defaultMessage:
		'There is an issue with the OAuth configuration of the connection to <link>{applicationName}</link>.',
	description:
		'The text is shown as a body paragraph in the status detail panel when there is an OAuth issue. The placeholder {applicationName} will be substituted with the name of the remote application (e.g. "Confluence", "Jira").',
});

const oauthTimestampRefusedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.oauth-timestamp-refused-title',
	defaultMessage: 'The system clocks are not synchronized',
	description: 'Title for OAuth timestamp refused message',
});

const oauthTimestampRefusedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.oauth-timestamp-refused-description',
	defaultMessage:
		'This server has a different system time from <link>{applicationName}</link>, which prevents the applications from authenticating.',
	description: 'Description for OAuth timestamp refused message',
});

const oauthSignatureInvalidTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.oauth-signature-invalid-title',
	defaultMessage: 'Invalid OAuth signature',
	description: 'Title for invalid OAuth signature message',
});

const oauthSignatureInvalidDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.oauth-signature-invalid-description',
	defaultMessage:
		"We couldn't connect to <link>{applicationName}</link>, possibly because that instance is behind a mis-configured proxy.",
	description: 'Description for invalid OAuth signature message',
});

const unexpectedResponseTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.unexpected-response-title',
	defaultMessage: 'Unexpected response',
	description:
		'The text is shown as a heading in the status detail panel for an application link when the remote application returns an unexpected or unparseable HTTP response.',
});

const unexpectedResponseDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.unexpected-response-description',
	defaultMessage:
		'We received an unexpected response from <link>{applicationName}</link>. If the remote application is behind a proxy, check that the application is operational. Troubleshoot this for other possible causes.',
	description: 'Description for unexpected response message',
});

const creationFailedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.creation-failed-status-title',
	defaultMessage: 'Creation failed',
	description:
		'The text is shown as a heading in the status detail panel for an application link when the OAuth 2.0 client creation failed during link setup.',
});

const creationFailedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.creation-failed-status-description',
	defaultMessage:
		'This application link failed to create an OAuth 2.0 client. Delete it and create a new one.',
	description:
		'The text is shown as a body paragraph in the status detail panel when the application link failed to create an OAuth 2.0 client, instructing the user to delete and re-create the link.',
});

const configurationIncompleteTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.configuration-incomplete-status-title',
	defaultMessage: 'Configuration incomplete',
	description: 'Title for configuration incomplete message',
});

const configurationIncompleteDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.configuration-incomplete-status-description',
	defaultMessage:
		'This application link is missing a remote client ID and remote client secret. Add these details to complete configuration.',
	description: 'Description for configuration incomplete message',
});

const inProgressTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.in-progress-status-title',
	defaultMessage: 'In progress',
	description:
		'The text is shown as a heading in the status detail panel for an application link that is still being configured and has not completed setup yet.',
});

const inProgressDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.in-progress-status-description',
	defaultMessage: "We're still configuring the application link.",
	description:
		'The text is shown as a body paragraph in the status detail panel when the application link setup is still in progress.',
});

const submissionFailedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.submission-failed-status-title',
	defaultMessage: 'Submission failed',
	description:
		'The text is shown as a heading in the status detail panel for an application link when the form submission to complete link configuration failed.',
});

const submissionFailedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.submission-failed-status-description',
	defaultMessage: 'Resubmit this application link to complete configuration.',
	description: 'Description for submission failed message',
});

const configurationCompleteTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.configuration-complete-status-title',
	defaultMessage: 'Configuration complete',
	description: 'Title for configuration complete message',
});

const configurationCompleteDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.configuration-complete-status-description',
	defaultMessage:
		'The configuration for your application link is complete. Try using it to confirm that everything works.',
	description: 'Description for configuration complete message',
});

const authLevelMismatchTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-mismatch-title',
	defaultMessage: 'OAuth mismatch',
	description:
		'The text is shown as a heading in the status detail panel for an application link when the local and remote OAuth authentication levels do not match each other.',
});

const noRemoteApplinkTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.no-remote-applink-title',
	defaultMessage: 'No remote application link',
	description:
		'The text is shown as a heading in the status detail panel for an application link when no reciprocal application link exists on the remote server.',
});

const noRemoteApplinkDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.no-remote-applink-description',
	defaultMessage:
		"We can't connect with <link>{applicationName}</link>  because a reciprocal application link is missing on that server. You'll need to delete and then re-create this application link.",
	description: 'Description for no remote applink message',
});

const remoteVersionIncompatibleTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.remote-version-incompatible-title',
	defaultMessage: 'Unable to retrieve status',
	description: 'Title for remote version incompatible message',
});

const remoteVersionIncompatibleDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.remote-version-incompatible-description',
	defaultMessage:
		"We're unable to determine the status of this link, because the version of {applicationName} is a little too old. The link may actually be good. You can upgrade <link>{applicationName}</link> to the latest version of {applicationType} to get status and diagnostic capabilities.",
	description: 'Description for remote version incompatible message',
});

const noOutgoingAuthTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.no-outgoing-auth-title',
	defaultMessage: 'No outgoing authentication',
	description:
		'The text is shown as a heading in the status detail panel for an application link when outgoing authentication is disabled, preventing the connection status from being displayed.',
});

const noOutgoingAuthDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.no-outgoing-auth-description',
	defaultMessage:
		"We can't display the status for this application link, because outgoing authentication is disabled. You can enable outgoing authentication to see the status.",
	description: 'Description for no outgoing auth message',
});

const genericLinkTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.generic-link-title',
	defaultMessage: 'Link to external third party',
	description:
		'The text is shown as a heading in the status detail panel for an application link pointing to a non-Atlassian external third-party application.',
});

const genericLinkDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.generic-link-description',
	defaultMessage:
		'This is an application link to an external third party. We are unable to retrieve the status to third parties. If you are experiencing any problems with your connection ensure that your OAuth configuration is correct.',
	description:
		'The text is shown as a body paragraph in the status detail panel for a generic third-party application link, explaining that status cannot be retrieved and advising the user to verify their OAuth configuration.',
});

const nonAtlassianTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.non-atlassian-title',
	defaultMessage: 'Link to external third party',
	description:
		'The text is shown as a heading in the status detail panel for an application link pointing to a non-Atlassian external third-party application.',
});

const nonAtlassianDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.non-atlassian-description',
	defaultMessage:
		'This is an application link to an external third party. We are unable to retrieve the status to third parties, so if you are experiencing any problems with your connection reach out to the third party directly.',
	description: 'Description for non-Atlassian link message',
});

const systemLinkTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.system-link-title',
	defaultMessage: 'Internal System Link',
	description:
		'The text is shown as a heading in the status detail panel for an application link that is an internal system link created automatically by the platform.',
});

const systemLinkDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.system-link-description',
	defaultMessage:
		'This is an internal link created automatically by the system. System internal links are read-only and cannot be edited.',
	description:
		'The text is shown as a body paragraph in the status detail panel explaining that the application link is an automatically created internal system link that is read-only and cannot be edited.',
});

const authLevelUnsupportedTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-unsupported-title',
	defaultMessage: 'Unsupported OAuth level',
	description: 'Title for unsupported OAuth level message',
});

const authLevelUnsupportedDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.auth-level-unsupported-description',
	defaultMessage:
		'The current OAuth configuration prevents integrations from working. You need to update your configuration to OAuth or OAuth with impersonation.',
	description: 'Description for unsupported OAuth level message',
});

const legacyUpdateTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.legacy-update-title',
	defaultMessage: 'Deprecated authentication type',
	description:
		'The text is shown as a heading in the status detail panel for an application link that uses a deprecated authentication type (e.g. Trusted Applications or Basic Access) and can be updated automatically.',
});

const legacyUpdateDescription1Message = defineMessage({
	id: 'applinks.administration.ui.common.status.legacy-update-description-1',
	defaultMessage:
		'This application link uses either the Trusted Applications or Basic Access authentication type, both of which are deprecated.',
	description: 'First part of description for legacy update message',
});

const legacyUpdateDescription2Message = defineMessage({
	id: 'applinks.administration.ui.common.status.legacy-update-description-2',
	defaultMessage: 'You should update this link to use OAuth authentication, the industry standard.',
	description: 'Second part of description for legacy update message',
});

const legacyRemovalTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.legacy-removal-title',
	defaultMessage: 'Deprecated authentication type',
	description:
		'The text is shown as a heading in the status detail panel for an application link that uses both OAuth and a deprecated authentication type, where the deprecated type should be removed.',
});

const legacyRemovalDescription1Message = defineMessage({
	id: 'applinks.administration.ui.common.status.legacy-removal-description-1',
	defaultMessage:
		'As well as OAuth, this application link still uses either the Trusted Applications or Basic Access authentication type, both of which are deprecated.',
	description: 'First part of description for legacy removal message',
});

const legacyRemovalDescription2Message = defineMessage({
	id: 'applinks.administration.ui.common.status.legacy-removal-description-2',
	defaultMessage: 'You should update this link to use OAuth authentication, the industry standard.',
	description: 'Second part of description for legacy removal message',
});

const manualLegacyUpdateTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.manual-legacy-update-title',
	defaultMessage: 'Deprecated authentication type',
	description:
		'The text is shown as a heading in the status detail panel for an application link that uses a deprecated authentication type and requires a manual update by the administrator to switch to OAuth.',
});

const manualLegacyUpdateDescription1Message = defineMessage({
	id: 'applinks.administration.ui.common.status.manual-legacy-update-description-1',
	defaultMessage:
		'This application link uses either the Trusted Applications or Basic Access authentication type, both of which are deprecated.',
	description: 'First part of description for manual legacy update message',
});

const manualLegacyUpdateDescription2Message = defineMessage({
	id: 'applinks.administration.ui.common.status.manual-legacy-update-description-2',
	defaultMessage:
		'You should manually update this link to use OAuth authentication, the industry standard.',
	description: 'Second part of description for manual legacy update message',
});

const manualLegacyRemovalTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.manual-legacy-removal-title',
	defaultMessage: 'Deprecated authentication type',
	description:
		'The text is shown as a heading in the status detail panel for an application link that uses a deprecated authentication type and requires a manual removal of that type from both ends of the link.',
});

const manualLegacyRemovalDescription1Message = defineMessage({
	id: 'applinks.administration.ui.common.status.manual-legacy-removal-description-1',
	defaultMessage: 'This application link uses a deprecated authentication type.',
	description: 'First part of description for manual legacy removal message',
});

const manualLegacyRemovalDescription2Message = defineMessage({
	id: 'applinks.administration.ui.common.status.manual-legacy-removal-description-2',
	defaultMessage:
		'You should click <bold>Update locally</bold> to move to OAuth authentication. Doing this will remove Trusted Applications authentication from this end of the link.',
	description: 'Second part of description for manual legacy removal message',
});

const manualLegacyRemovalDescription3Message = defineMessage({
	id: 'applinks.administration.ui.common.status.manual-legacy-removal-description-3',
	defaultMessage:
		"You'll then need to manually remove Trusted Applications authentication from the <link>{applicationName}</link> end of the link.",
	description: 'Third part of description for manual legacy removal message',
});

const unknownTitleMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.unknown-title',
	defaultMessage: 'Network error',
	description:
		'The text is shown as a heading in the status detail panel for an application link when an unrecognised network or configuration error prevents the connection.',
});

const unknownDescriptionMessage = defineMessage({
	id: 'applinks.administration.ui.common.status.unknown-description',
	defaultMessage:
		"We couldn't connect to {applicationName} at {rpcUrl}. This might have been caused by a network error or another configuration error.",
	description:
		'The text is shown as a body paragraph in the status detail panel for an unknown connection error. The placeholder {applicationName} will be substituted with the name of the remote application, and {rpcUrl} will be substituted with the remote application URL.',
});

const messages = {
	cloudConnectorAwaitingDcResponseTitle: cloudConnectorAwaitingDcResponseTitleMessage,
	cloudConnectorAwaitingDcResponseDescription: cloudConnectorAwaitingDcResponseDescriptionMessage,
	connectionRefusedTitle: connectionRefusedTitleMessage,
	connectionRefusedDescription: connectionRefusedDescriptionMessage,
	unknownHostTitle: unknownHostTitleMessage,
	unknownHostDescription: unknownHostDescriptionMessage,
	sslUntrustedTitle: sslUntrustedTitleMessage,
	sslUntrustedDescription: sslUntrustedDescriptionMessage,
	sslHostnameUnmatchedTitle: sslHostnameUnmatchedTitleMessage,
	sslHostnameUnmatchedDescription: sslHostnameUnmatchedDescriptionMessage,
	sslUnmatchedTitle: sslUnmatchedTitleMessage,
	sslUnmatchedDescription: sslUnmatchedDescriptionMessage,
	oauthProblemTitle: oauthProblemTitleMessage,
	oauthProblemDescription: oauthProblemDescriptionMessage,
	oauthTimestampRefusedTitle: oauthTimestampRefusedTitleMessage,
	oauthTimestampRefusedDescription: oauthTimestampRefusedDescriptionMessage,
	oauthSignatureInvalidTitle: oauthSignatureInvalidTitleMessage,
	oauthSignatureInvalidDescription: oauthSignatureInvalidDescriptionMessage,
	unexpectedResponseTitle: unexpectedResponseTitleMessage,
	unexpectedResponseDescription: unexpectedResponseDescriptionMessage,
	creationFailedTitle: creationFailedTitleMessage,
	creationFailedDescription: creationFailedDescriptionMessage,
	configurationIncompleteTitle: configurationIncompleteTitleMessage,
	configurationIncompleteDescription: configurationIncompleteDescriptionMessage,
	inProgressTitle: inProgressTitleMessage,
	inProgressDescription: inProgressDescriptionMessage,
	submissionFailedTitle: submissionFailedTitleMessage,
	submissionFailedDescription: submissionFailedDescriptionMessage,
	configurationCompleteTitle: configurationCompleteTitleMessage,
	configurationCompleteDescription: configurationCompleteDescriptionMessage,
	authLevelMismatchTitle: authLevelMismatchTitleMessage,
	noRemoteApplinkTitle: noRemoteApplinkTitleMessage,
	noRemoteApplinkDescription: noRemoteApplinkDescriptionMessage,
	remoteVersionIncompatibleTitle: remoteVersionIncompatibleTitleMessage,
	remoteVersionIncompatibleDescription: remoteVersionIncompatibleDescriptionMessage,
	noOutgoingAuthTitle: noOutgoingAuthTitleMessage,
	noOutgoingAuthDescription: noOutgoingAuthDescriptionMessage,
	genericLinkTitle: genericLinkTitleMessage,
	genericLinkDescription: genericLinkDescriptionMessage,
	nonAtlassianTitle: nonAtlassianTitleMessage,
	nonAtlassianDescription: nonAtlassianDescriptionMessage,
	systemLinkTitle: systemLinkTitleMessage,
	systemLinkDescription: systemLinkDescriptionMessage,
	authLevelUnsupportedTitle: authLevelUnsupportedTitleMessage,
	authLevelUnsupportedDescription: authLevelUnsupportedDescriptionMessage,
	legacyUpdateTitle: legacyUpdateTitleMessage,
	legacyUpdateDescription1: legacyUpdateDescription1Message,
	legacyUpdateDescription2: legacyUpdateDescription2Message,
	legacyRemovalTitle: legacyRemovalTitleMessage,
	legacyRemovalDescription1: legacyRemovalDescription1Message,
	legacyRemovalDescription2: legacyRemovalDescription2Message,
	manualLegacyUpdateTitle: manualLegacyUpdateTitleMessage,
	manualLegacyUpdateDescription1: manualLegacyUpdateDescription1Message,
	manualLegacyUpdateDescription2: manualLegacyUpdateDescription2Message,
	manualLegacyRemovalTitle: manualLegacyRemovalTitleMessage,
	manualLegacyRemovalDescription1: manualLegacyRemovalDescription1Message,
	manualLegacyRemovalDescription2: manualLegacyRemovalDescription2Message,
	manualLegacyRemovalDescription3: manualLegacyRemovalDescription3Message,
	unknownTitle: unknownTitleMessage,
	unknownDescription: unknownDescriptionMessage,
};

type ErrorStatusSectionMessageProps = {
	err: ApplinksError;
	context: ApplicationLinkV3Status;
};

type StatusAction = 'auth' | 'edit' | 'update';

type ErrorContents = {
	title: string;
	description: ReactNode;
	action?: StatusAction;
};

export const ErrorStatusSectionMessage = (
	props: ErrorStatusSectionMessageProps,
): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const buildErrorContents = useCallback((): ErrorContents => {
		if (fg('cloud-connector-applinks-migration-m1-jira-srs')) {
			// FG tidy note: merge it to the main switch clause below
			switch (props.err.type) {
				case 'CLOUD_CONNECTOR_AWAITING_DC_RESPONSE': {
					const dcCloudConnectorTabUrl = `${props.context.link.displayUrl}/plugins/servlet/applinks/listApplicationLinks#tabs-cloud-connections`;
					return {
						title: formatMessage(messages.cloudConnectorAwaitingDcResponseTitle),
						description: formatMessage(messages.cloudConnectorAwaitingDcResponseDescription, {
							applicationName: props.context.link.name,
							link: (chunks: ReactNode[]) => (
								<Link href={dcCloudConnectorTabUrl} target="_blank">
									{chunks}
								</Link>
							),
						}),
					};
				}
				default:
			}
		}

		switch (props.err.type) {
			case 'CONNECTION_REFUSED':
				return {
					title: formatMessage(messages.connectionRefusedTitle),
					description: formatMessage(messages.connectionRefusedDescription, {
						rpcUrl: props.context.link.rpcUrl,
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
						code: (chunks: ReactNode[]) => <Code>{chunks}</Code>,
					}),
					action: 'edit',
				};
			case 'UNKNOWN_HOST':
				return {
					title: formatMessage(messages.unknownHostTitle),
					description: formatMessage(messages.unknownHostDescription, {
						rpcUrl: props.context.link.rpcUrl,
						code: (chunks: ReactNode[]) => <Code>{chunks}</Code>,
					}),
					action: 'edit',
				};
			case 'SSL_UNTRUSTED':
				return {
					title: formatMessage(messages.sslUntrustedTitle),
					description: formatMessage(messages.sslUntrustedDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'SSL_HOSTNAME_UNMATCHED':
				return {
					title: formatMessage(messages.sslHostnameUnmatchedTitle),
					description: formatMessage(messages.sslHostnameUnmatchedDescription, {
						applicationName: props.context.link.name,
						rpcUrl: <Code>{props.context.link.rpcUrl}</Code>,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'SSL_UNMATCHED':
				return {
					title: formatMessage(messages.sslUnmatchedTitle),
					description: formatMessage(messages.sslUnmatchedDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_PROBLEM':
				return {
					title: formatMessage(messages.oauthProblemTitle),
					description: formatMessage(messages.oauthProblemDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_TIMESTAMP_REFUSED':
				return {
					title: formatMessage(messages.oauthTimestampRefusedTitle),
					description: formatMessage(messages.oauthTimestampRefusedDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'OAUTH_SIGNATURE_INVALID':
				return {
					title: formatMessage(messages.oauthSignatureInvalidTitle),
					description: formatMessage(messages.oauthSignatureInvalidDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'UNEXPECTED_RESPONSE':
				return {
					title: formatMessage(messages.unexpectedResponseTitle),
					description: formatMessage(messages.unexpectedResponseDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'UNEXPECTED_RESPONSE_STATUS':
				return {
					title: formatMessage(messages.unexpectedResponseTitle),
					description: formatMessage(messages.unexpectedResponseDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'CREATION_FAILED':
				return {
					title: formatMessage(messages.creationFailedTitle),
					description: formatMessage(messages.creationFailedDescription),
				};
			case 'CONFIGURATION_INCOMPLETE':
				return {
					title: formatMessage(messages.configurationIncompleteTitle),
					description: formatMessage(messages.configurationIncompleteDescription),
				};
			case 'IN_PROGRESS':
				return {
					title: formatMessage(messages.inProgressTitle),
					description: formatMessage(messages.inProgressDescription),
				};
			case 'SUBMISSION_FAILED':
				return {
					title: formatMessage(messages.submissionFailedTitle),
					description: formatMessage(messages.submissionFailedDescription),
				};
			case 'CONFIGURATION_COMPLETE':
				return {
					title: formatMessage(messages.configurationCompleteTitle),
					description: formatMessage(messages.configurationCompleteDescription),
				};
			case 'AUTH_LEVEL_MISMATCH': {
				return {
					title: formatMessage(messages.authLevelMismatchTitle),
					description: <AuthLevelMismatchDescription status={props.context} />,
					action: 'edit',
				};
			}
			case 'NO_REMOTE_APPLINK':
				return {
					title: formatMessage(messages.noRemoteApplinkTitle),
					description: formatMessage(messages.noRemoteApplinkDescription, {
						applicationName: props.context.link.name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'INSUFFICIENT_REMOTE_PERMISSION':
			case 'REMOTE_VERSION_INCOMPATIBLE':
				return {
					title: formatMessage(messages.remoteVersionIncompatibleTitle),
					description: formatMessage(messages.remoteVersionIncompatibleDescription, {
						applicationName: props.context.link.name,
						// TODO: Try determine whether the link is DC or not (it doesn't strictly matter, as it'll still say "Confluence", etc)
						applicationType: lookupTypeByTypeId(props.context.link.type, false).name,
						link: (chunks: ReactNode[]) => (
							<Link href={props.context.link.displayUrl} target="_blank">
								{chunks}
							</Link>
						),
					}),
				};
			case 'NO_OUTGOING_AUTH':
				return {
					title: formatMessage(messages.noOutgoingAuthTitle),
					description: formatMessage(messages.noOutgoingAuthDescription),
				};
			case 'GENERIC_LINK':
				return {
					title: formatMessage(messages.genericLinkTitle),
					description: formatMessage(messages.genericLinkDescription),
				};
			case 'NON_ATLASSIAN':
				return {
					title: formatMessage(messages.nonAtlassianTitle),
					description: formatMessage(messages.nonAtlassianDescription),
				};
			case 'SYSTEM_LINK':
				return {
					title: formatMessage(messages.systemLinkTitle),
					description: formatMessage(messages.systemLinkDescription),
				};
			case 'AUTH_LEVEL_UNSUPPORTED':
				return {
					title: formatMessage(messages.authLevelUnsupportedTitle),
					description: formatMessage(messages.authLevelUnsupportedDescription),
					action: 'edit',
				};
			case 'LEGACY_UPDATE':
				return {
					title: formatMessage(messages.legacyUpdateTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.legacyUpdateDescription1)}</Text>
							<Text>{formatMessage(messages.legacyUpdateDescription2)}</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'LEGACY_REMOVAL':
				return {
					title: formatMessage(messages.legacyRemovalTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.legacyRemovalDescription1)}</Text>
							<Text>{formatMessage(messages.legacyRemovalDescription2)}</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'MANUAL_LEGACY_UPDATE':
				return {
					title: formatMessage(messages.manualLegacyUpdateTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.manualLegacyUpdateDescription1)}</Text>
							<Text>{formatMessage(messages.manualLegacyUpdateDescription2)}</Text>
						</Stack>
					),
				};
			case 'MANUAL_LEGACY_REMOVAL':
				return {
					title: formatMessage(messages.manualLegacyRemovalTitle),
					description: (
						<Stack space="space.100">
							<Text>{formatMessage(messages.manualLegacyRemovalDescription1)}</Text>
							<Text>
								{formatMessage(messages.manualLegacyRemovalDescription2, {
									bold: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
								})}
							</Text>
							<Text>
								{formatMessage(messages.manualLegacyRemovalDescription3, {
									applicationName: props.context.link.name,
									link: (chunks: ReactNode[]) => (
										<Link href={props.context.link.displayUrl} target="_blank">
											{chunks}
										</Link>
									),
								})}
							</Text>
						</Stack>
					),
					action: 'update',
				};
			case 'UNKNOWN':
			default:
				return {
					title: formatMessage(messages.unknownTitle),
					description: formatMessage(messages.unknownDescription, {
						applicationName: props.context.link.name,
						rpcUrl: <Code>{props.context.link.rpcUrl}</Code>,
					}),
					action: 'edit',
				};
		}
	}, [props.err, props.context, formatMessage]);

	const buildActions = useCallback(() => {
		switch (buildErrorContents().action) {
			case 'edit': {
				// We don't show edit actions on the edit page
				// However when this class is extended to support popups on the list page, we'll show it there
				return [];
			}
			case 'update': {
				return [];
				// return [
				// 	<SectionMessageAction key="action-update" href="#">
				// 		{formatMessage(messages.actionUpdateNonfinal)}
				// 	</SectionMessageAction>,
				// ];
			}
			default:
				return [];
		}
	}, [buildErrorContents]);

	return (
		<SectionMessage appearance="error" title={buildErrorContents().title} actions={buildActions()}>
			<Stack space="space.100">
				<Text>{buildErrorContents().description}</Text>
				{props.context.error && props.context.error.details && (
					<CodeBlock text={props.context.error.details} />
				)}
			</Stack>
		</SectionMessage>
	);
};

export default ErrorStatusSectionMessage;
